import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Award, Calendar, CalendarDays, Hammer, MessageSquare, UserPlus } from "lucide-react";

import { api } from "@/lib/api";
import { AppointmentItem } from "@/components/appointment-item";
import { MasterCard } from "@/components/masters";
import { FileList, StatusTimeline } from "@/components/dossier-parts";
import { Header } from "@/components/header";
import { Badge, Button, Card, ErrorView, Field, Loading, Row } from "@/components/kit";
import { Sheet } from "@/components/sheet";
import { useToast } from "@/components/toast";
import { C, COMMISSION_LABELS, commissionColor, dateTime, eur, PROJECT_LABELS, STATUS_LABELS, statusColor } from "@/lib/format";

const EDITABLE = ["recu", "contacte", "etudie", "devis"];

function StatusEditor({ d, busy, run, id, onContract }) {
  const [note, setNote] = useState("");
  return (
    <Card className="flex flex-col gap-3">
      <h2 className="t-h3">Modifier le statut</h2>
      <div className="flex flex-wrap gap-2">
        {EDITABLE.map((s) => {
          const active = s === d.status;
          return (
            <button key={s} type="button" data-testid={`admin-status-${s}`} disabled={busy !== null || active} aria-pressed={active}
              onClick={() => run("status", `/admin/recommendations/${id}/status`, "PUT", { status: s, note: note.trim() || null }, "Statut mis à jour.").then((ok) => ok && setNote(""))}
              className={`focus-ring h-10 rounded-full border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed ${active ? "border-[#D4AF37] bg-[#292415] text-[#D4AF37]" : "border-[#333333] bg-[#2A2A2A] text-[#CCCCCC] enabled:hover:border-[#D4AF37]/60 disabled:opacity-60"}`}>
              {STATUS_LABELS[s]}
            </button>
          );
        })}
      </div>
      <Field testID="admin-status-note-input" label="Note visible par l'agent (facultatif)" value={note} onChangeText={setNote} />
      <div className="flex gap-2">
        <Button testID="admin-rdv-toggle" title={d.rdv_done ? "Annuler RDV" : "RDV réalisé"} icon={Calendar} variant="secondary" className="flex-1" loading={busy === "rdv"}
          onClick={() => run("rdv", `/admin/recommendations/${id}/rdv`, "PUT", { done: !d.rdv_done }, "Rendez-vous mis à jour.")} />
        <Button testID="admin-open-contract" title="Contrat signé" icon={Award} variant="success" className="flex-1" onClick={onContract} />
      </div>
    </Card>
  );
}

function Comments({ d, busy, run, id }) {
  const [comment, setComment] = useState("");
  return (
    <Card className="flex flex-col gap-3">
      <h2 className="t-h3">Commentaires internes</h2>
      <p className="t-caption">Non visibles par l'agent.</p>
      {d.comments.map((c) => (
        <div key={c.id} className="flex flex-col gap-0.5 rounded-xl bg-[#2A2A2A] p-3" data-testid={`admin-comment-${c.id}`}>
          <p className="whitespace-pre-line text-sm text-white">{c.text}</p>
          <p className="t-caption">{c.by} · {dateTime(c.at)}</p>
        </div>
      ))}
      <Field testID="admin-comment-input" label="Nouveau commentaire" value={comment} onChangeText={setComment} multiline />
      <Button testID="admin-comment-submit" title="Ajouter" icon={MessageSquare} variant="secondary" loading={busy === "comment"} disabled={!comment.trim()}
        onClick={() => run("comment", `/admin/recommendations/${id}/comments`, "POST", { text: comment }, "Commentaire ajouté.").then((ok) => ok && setComment(""))} />
    </Card>
  );
}

function ContractSheet({ open, onClose, d, busy, run, id }) {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const amountNum = Number(amount.replace(/\s/g, "").replace(",", "."));
  const preview = (() => {
    if (!d?.agent_tiers?.length || !amountNum) return null;
    const sorted = [...d.agent_tiers].sort((a, b) => a.min - b.min);
    const t = sorted.find((x) => amountNum >= x.min && (x.max === null || amountNum < x.max)) ?? sorted[sorted.length - 1];
    return { rate: t.percent, value: Math.round(amountNum * t.percent) / 100 };
  })();
  return (
    <Sheet visible={open} onClose={onClose} title="Enregistrer le contrat signé" testID="contract-sheet">
      <div className="flex flex-col gap-4">
        <Field testID="contract-amount-input" label="Montant signé (€ TTC)" inputMode="decimal" value={amount} onChangeText={setAmount} placeholder="220000" />
        {preview && (
          <p data-testid="contract-commission-preview" className="t-body">
            Commission calculée : <span className="t-h3 !text-[#27AE60]">{eur(preview.value)}</span> ({String(preview.rate).replace(".", ",")} % · barème de l'agent)
          </p>
        )}
        <p className="t-caption">La commission sera créée « en attente de validation ». Si l'agent a un parrain, une récompense de 50 € sera générée.</p>
        {d && !d.agent_tiers_custom ? (
          <div className="flex flex-col gap-2">
            <p data-testid="contract-no-tiers" className="text-sm text-[#F2994A]">Cet agent n'a pas encore de barème de commission. Définissez-le depuis sa fiche avant d'enregistrer le contrat.</p>
            {d.agent && <Button testID="contract-go-agent-tiers" title="Définir le barème de l'agent" variant="secondary" onClick={() => { onClose(); navigate(`/admin/agent/${d.agent.id}`); }} />}
          </div>
        ) : null}
        <Button testID="contract-confirm-button" title="Confirmer la signature" variant="success" loading={busy === "contract"} disabled={!amountNum || amountNum <= 0 || !d?.agent_tiers_custom}
          onClick={async () => { if (await run("contract", `/admin/recommendations/${id}/contract`, "POST", { amount: amountNum }, "Contrat enregistré.")) { onClose(); setAmount(""); } }} />
      </div>
    </Sheet>
  );
}

function Intervenants({ d, onMaster }) {
  const m = d.master_detail;
  return (
    <Card testID="admin-dossier-intervenants" className="flex flex-col gap-3">
      <h2 className="t-h3">Intervenants &amp; commissions</h2>
      <div className="flex flex-col gap-0.5">
        <p className="t-caption">AGENT QUI A APPORTÉ LE CLIENT</p>
        <p className="text-sm font-medium text-white" data-testid="admin-dossier-intervenant-agent">{d.agent?.name ?? d.agent_name ?? "Agent supprimé"}</p>
        <p className="t-caption" data-testid="admin-dossier-agent-commission">
          {d.commission
            ? `Commission : ${eur(d.commission.amount)} (${String(d.commission.rate).replace(".", ",")} %) · ${COMMISSION_LABELS[d.commission.status]}`
            : d.agent_tiers_custom ? "Commission calculée à la signature selon son barème" : "Barème de l'agent à définir"}
        </p>
      </div>
      <div className="h-px bg-[#222222]" />
      <div className="flex flex-col gap-0.5">
        <p className="t-caption">MAÎTRE D'ŒUVRE QUI PREND EN CHARGE LE PROJET</p>
        {m ? (
          <Link to={`/admin/master/${m.id}`} data-testid="admin-dossier-master-link" className="focus-ring -mx-2 flex flex-col gap-1 rounded-xl px-2 py-1 hover:bg-white/5">
            <span className="text-sm font-medium text-white" data-testid="admin-dossier-master-name">{m.company_name}</span>
            <span className="t-caption">{m.city} · {m.phone ?? "—"}</span>
            <Badge label={m.insurance_ok ? "Assurances vérifiées" : "Assurances à vérifier"} color={m.insurance_ok ? C.success : C.warning} />
          </Link>
        ) : (
          <p className="text-sm text-[#F2994A]" data-testid="admin-dossier-master-name">Non attribué{d.department_name ? ` · projet en ${d.department_name} (${d.department})` : ""}</p>
        )}
        {m ? (
          <p className="t-caption" data-testid="admin-dossier-master-commission">
            Commission : {d.master_commission_amount !== null && d.master_commission_amount !== undefined ? eur(d.master_commission_amount) : "à définir selon le chantier"}
          </p>
        ) : null}
      </div>
      <Button testID="admin-dossier-master-button" title={m ? "Modifier maître d'œuvre / commission" : "Attribuer un maître d'œuvre"} icon={Hammer} variant="secondary" onClick={onMaster} />
    </Card>
  );
}

function Appointments({ d }) {
  const navigate = useNavigate();
  const list = d.appointments ?? [];
  const label = `${d.number} · ${d.client.first_name} ${d.client.last_name}`;
  return (
    <Card testID="admin-dossier-appointments" className="flex flex-col gap-3">
      <h2 className="t-h3">Rendez-vous ({list.length})</h2>
      {list.map((a) => <AppointmentItem key={a.id} a={a} showDate hideDossier />)}
      <Button testID="admin-dossier-plan-button" title="Planifier un rendez-vous" icon={CalendarDays} variant="secondary"
        onClick={() => navigate(`/admin/appointment-form?dossier_id=${d.id}&dossier_label=${encodeURIComponent(label)}`)} />
    </Card>
  );
}

function MasterSheet({ open, onClose, d, busy, run, id }) {
  const [all, setAll] = useState(false);
  const [pick, setPick] = useState(null);
  const [amount, setAmount] = useState("");
  const dept = d?.department;
  useEffect(() => {
    if (!open || !d) return;
    setPick(d.master_detail?.id ?? null);
    const a = d.master_commission_amount;
    setAmount(a === null || a === undefined ? "" : String(a).replace(".", ","));
    setAll(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const masters = useQuery({
    queryKey: ["admin-masters", "", all || !dept ? "all" : dept],
    queryFn: () => api(`/admin/masters${all || !dept ? "" : `?department=${dept}`}`),
    enabled: open,
  });
  const num = amount.trim() ? Number(amount.replace(/\s/g, "").replace(",", ".")) : null;
  const amountOk = num === null || (!isNaN(num) && num >= 0);
  const options = (masters.data ?? []).filter((m) => m.status === "active" || m.id === d?.master_detail?.id);
  return (
    <Sheet visible={open} onClose={onClose} title="Maître d'œuvre du projet" testID="master-sheet">
      <div className="flex flex-col gap-3">
        <p className="t-caption">{all || !dept ? "Tous les maîtres d'œuvre actifs" : `Maîtres d'œuvre couvrant le ${dept}${d?.department_name ? ` (${d.department_name})` : ""}`}</p>
        <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto pr-1">
          {masters.isLoading ? <Loading /> : options.map((m) => (
            <MasterCard key={m.id} m={m} testID={`master-option-${m.id}`} selected={pick === m.id} onClick={() => setPick(m.id)} />
          ))}
          {!masters.isLoading && !(masters.data ?? []).length ? (
            <p data-testid="master-sheet-empty" className="text-sm text-[#F2994A]">Aucun maître d'œuvre ne couvre ce département : pensez à en recruter.</p>
          ) : null}
        </div>
        {dept ? <Button testID="master-sheet-toggle-all" title={all ? `Seulement le ${dept}` : "Voir tous les maîtres d'œuvre"} variant="ghost" onClick={() => setAll(!all)} /> : null}
        <Field testID="master-commission-amount-input" label="Commission du maître d'œuvre (€) — selon le chantier" inputMode="decimal" value={amount} onChangeText={setAmount} placeholder="Laisser vide si à définir"
          error={amountOk ? undefined : "Montant invalide"} />
        <Button testID="master-assign-confirm" title="Enregistrer" disabled={!pick || !amountOk} loading={busy === "master"}
          onClick={async () => { if (await run("master", `/admin/recommendations/${id}/master`, "PUT", { master_id: pick, commission_amount: num }, "Maître d'œuvre enregistré.")) onClose(); }} />
        {d?.master_detail && (
          <Button testID="master-assign-clear" title="Retirer le maître d'œuvre" variant="ghost"
            onClick={async () => { if (await run("master", `/admin/recommendations/${id}/master`, "PUT", { master_id: null }, "Maître d'œuvre retiré.")) onClose(); }} />
        )}
      </div>
    </Sheet>
  );
}

function AssignSheet({ open, onClose, d, staff, run, id }) {
  return (
    <Sheet visible={open} onClose={onClose} title="Attribuer à un collaborateur" testID="assign-sheet">
      <div className="flex flex-col gap-2">
        {(staff ?? []).map((s) => (
          <Button key={s.id} testID={`assign-option-${s.id}`} title={`${s.name}${s.permission === "super" ? " (admin)" : ""}`} variant={d?.assigned_to?.id === s.id ? "primary" : "secondary"}
            onClick={async () => { if (await run("assign", `/admin/recommendations/${id}/assign`, "PUT", { collaborator_id: s.id }, `Attribué à ${s.name}.`)) onClose(); }} />
        ))}
        {d?.assigned_to && (
          <Button testID="assign-clear" title="Retirer l'attribution" variant="ghost"
            onClick={async () => { if (await run("assign", `/admin/recommendations/${id}/assign`, "PUT", { collaborator_id: null }, "Attribution retirée.")) onClose(); }} />
        )}
      </div>
    </Sheet>
  );
}

export default function AdminDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-dossier", id], queryFn: () => api(`/admin/recommendations/${id}`) });
  const staff = useQuery({ queryKey: ["admin-staff"], queryFn: () => api("/admin/staff") });
  const [busy, setBusy] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [masterOpen, setMasterOpen] = useState(false);

  const run = async (key, path, method, body, msg) => {
    setBusy(key);
    try {
      const d = await api(path, { method, body });
      qc.setQueryData(["admin-dossier", id], d);
      ["admin-dossiers", "admin-dashboard", "admin-commissions", "monthly", "admin-masters", "admin-master", "admin-clients", "admin-geo"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast(msg);
      return true;
    } catch (e) {
      toast(e.message, "error");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const d = q.data;
  const signed = d ? !EDITABLE.includes(d.status) : false;
  return (
    <div data-testid="admin-dossier-screen" className="flex flex-col">
      <Header title={d?.number ?? "Dossier"} subtitle={d ? `Reçu le ${dateTime(d.created_at)}` : undefined} />
      {q.isLoading ? <Loading /> : q.isError || !d ? <ErrorView message="Dossier introuvable." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto grid w-full max-w-6xl items-start gap-4 p-4 pb-8 md:p-8 lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <Card className="flex flex-col gap-2" style={{ borderColor: statusColor(d.status) }}>
              <p className="t-caption">ÉTAT D'AVANCEMENT</p>
              <p data-testid="admin-dossier-status" className="t-h1" style={{ color: statusColor(d.status) }}>{STATUS_LABELS[d.status]}</p>
              <div className="flex flex-wrap gap-2">
                <Badge testID="admin-dossier-rdv" label={d.rdv_done ? "RDV réalisé" : (d.appointments ?? []).length ? "RDV planifié" : "RDV à planifier"}
                  color={d.rdv_done ? C.success : (d.appointments ?? []).length ? C.brand : C.silver} />
                <Badge testID="admin-dossier-assigned" label={d.assigned_to ? `Attribué à ${d.assigned_to.name}` : "Non attribué"} color={C.brand} />
              </div>
            </Card>
            <Intervenants d={d} onMaster={() => setMasterOpen(true)} />
            <Appointments d={d} />
            {!signed && <StatusEditor d={d} busy={busy} run={run} id={id} onContract={() => setContractOpen(true)} />}
            {d.commission && (
              <Card className="flex flex-col gap-1">
                <h2 className="t-h3">Commission associée</h2>
                <Row label="Montant signé" value={eur(d.contract_amount)} />
                <Row label="Taux appliqué" value={`${String(d.commission.rate).replace(".", ",")} %`} />
                <Row label="Commission" value={eur(d.commission.amount)} testID="admin-dossier-commission" />
                <div className="mt-2"><Badge label={COMMISSION_LABELS[d.commission.status]} color={commissionColor(d.commission.status)} /></div>
                <Button testID="admin-dossier-go-finance" title="Gérer dans Finances" variant="ghost" onClick={() => navigate("/admin-finance")} />
              </Card>
            )}
            <Card className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <h2 className="t-h3">Agent</h2>
                <Button testID="admin-assign-button" title="Attribuer" icon={UserPlus} variant="secondary" onClick={() => setAssignOpen(true)} />
              </div>
              {d.agent ? (
                <Link to={`/admin/agent/${d.agent.id}`} data-testid="admin-dossier-agent-link" className="focus-ring -mx-2 rounded-xl px-2 py-1 transition-colors hover:bg-white/5">
                  <Row label="Nom" value={d.agent.name} />
                  <Row label="Agence" value={d.agent.agency} />
                  <Row label="Téléphone" value={d.agent.phone} />
                  <Row label="E-mail" value={d.agent.email} />
                  <p className="mt-1 text-sm text-[#D4AF37]">Voir la fiche agent →</p>
                </Link>
              ) : <p className="t-caption">Agent supprimé</p>}
            </Card>
            <Card>
              <h2 className="t-h3 mb-2">Client</h2>
              <Row label="Nom" value={`${d.client.first_name} ${d.client.last_name}`} testID="admin-dossier-client" />
              <Row label="Téléphone" value={d.client.phone} />
              <Row label="E-mail" value={d.client.email} />
              <Row label="Adresse" value={`${d.address}, ${d.postal_code} ${d.city}`} />
              <Row label="Projet" value={PROJECT_LABELS[d.project_type]} />
              <Row label="Prestation" value={d.service_type} />
              <Row label="Budget estimatif" value={d.budget ? eur(d.budget) : null} />
              <p className="t-label mt-3">Description</p>
              <p className="t-body mt-1 whitespace-pre-line">{d.description}</p>
            </Card>
          </div>
          <div className="flex flex-col gap-4">
            <Card>
              <h2 className="t-h3 mb-2">Documents &amp; photos</h2>
              <FileList files={d.files} />
            </Card>
            <Comments d={d} busy={busy} run={run} id={id} />
            <Card>
              <h2 className="t-h3 mb-3">Évolution</h2>
              <StatusTimeline current={d.status} history={d.status_history} />
            </Card>
            <Card className="flex flex-col gap-2" testID="admin-dossier-history">
              <h2 className="t-h3">Historique des modifications</h2>
              {[...d.history].reverse().map((h, i) => (
                <div key={i} className="flex gap-2">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#D4AF37]" />
                  <div className="flex-1">
                    <p className="text-sm text-white">{h.action}</p>
                    <p className="t-caption">{h.by} · {dateTime(h.at)}</p>
                  </div>
                </div>
              ))}
            </Card>
          </div>
        </div>
      )}
      <AssignSheet open={assignOpen} onClose={() => setAssignOpen(false)} d={d} staff={staff.data} run={run} id={id} />
      <MasterSheet open={masterOpen} onClose={() => setMasterOpen(false)} d={d} busy={busy} run={run} id={id} />
      <ContractSheet open={contractOpen} onClose={() => setContractOpen(false)} d={d} busy={busy} run={run} id={id} />
    </div>
  );
}
